import {
	AssistantAvatarColor,
	type AssistantAvatarConfig,
	AssistantAvatarEyes,
	AssistantAvatarFacialHair,
	AssistantAvatarHair,
	AssistantAvatarHairColor,
	AssistantAvatarKind,
	AssistantAvatarShape,
	AssistantAvatarSkinTone,
	AssistantCharacterAccessory,
	type AssistantCharacterAvatar,
	AssistantHumanAccessory,
	type AssistantHumanAvatar,
} from "@/ui/AssistantAvatar/assistantAvatarConfig";

/** Where every new design starts, and where Reset returns the draft to. */
export const DEFAULT_CHARACTER_AVATAR: AssistantCharacterAvatar = {
	kind: AssistantAvatarKind.Character,
	shape: AssistantAvatarShape.Blob,
	color: AssistantAvatarColor.Lavender,
	eyes: AssistantAvatarEyes.Round,
	accessory: AssistantCharacterAccessory.None,
};

/** Where the human style starts; reads neutral. */
export const DEFAULT_HUMAN_AVATAR: AssistantHumanAvatar = {
	kind: AssistantAvatarKind.Human,
	skin_tone: AssistantAvatarSkinTone.Tone3,
	hair: AssistantAvatarHair.Short,
	hair_color: AssistantAvatarHairColor.Brown,
	facial_hair: AssistantAvatarFacialHair.None,
	color: AssistantAvatarColor.Lavender,
	eyes: AssistantAvatarEyes.Dots,
	accessory: AssistantHumanAccessory.Stethoscope,
};

export enum AnnieAvatarPresetId {
	Classic = "classic",
	MidnightGhost = "midnight_ghost",
	CoralDrop = "coral_drop",
	LongAuburn = "long_auburn",
	ChestnutBun = "chestnut_bun",
	ShortBeard = "short_beard",
	BuzzMustache = "buzz_mustache",
	CurlyGlasses = "curly_glasses",
	PinkShades = "pink_shades",
	HalloweenWitch = "halloween_witch",
	HalloweenPumpkin = "halloween_pumpkin",
	HalloweenGhost = "halloween_ghost",
	HalloweenVampire = "halloween_vampire",
}

export interface AnnieAvatarPreset {
	id: AnnieAvatarPresetId;
	config: AssistantAvatarConfig;
}

const HALLOWEEN_WITCH_AVATAR: AssistantCharacterAvatar = { ...DEFAULT_CHARACTER_AVATAR, accessory: AssistantCharacterAccessory.WitchHat };

const HALLOWEEN_PUMPKIN_AVATAR: AssistantCharacterAvatar = {
	kind: AssistantAvatarKind.Character,
	shape: AssistantAvatarShape.Blob,
	color: AssistantAvatarColor.Pumpkin,
	eyes: AssistantAvatarEyes.Happy,
	accessory: AssistantCharacterAccessory.PumpkinStem,
};

/** What people who never designed Annie see, and where a new design starts: the pumpkin in Halloween week, else Classic. */
export const defaultCharacterAvatar = (isHalloweenSeason: boolean): AssistantCharacterAvatar =>
	isHalloweenSeason ? HALLOWEEN_PUMPKIN_AVATAR : DEFAULT_CHARACTER_AVATAR;

const human = (overrides: Partial<Omit<AssistantHumanAvatar, "kind">>): AssistantHumanAvatar => ({ ...DEFAULT_HUMAN_AVATAR, ...overrides });

/** Gender-balanced humans: two feminine-coded, two masculine-coded, two neutral. */
export const ANNIE_AVATAR_PRESET_CONFIGS: Record<AnnieAvatarPresetId, AssistantAvatarConfig> = {
	[AnnieAvatarPresetId.Classic]: DEFAULT_CHARACTER_AVATAR,
	[AnnieAvatarPresetId.MidnightGhost]: {
		kind: AssistantAvatarKind.Character,
		shape: AssistantAvatarShape.Ghost,
		color: AssistantAvatarColor.Midnight,
		eyes: AssistantAvatarEyes.Stars,
		accessory: AssistantCharacterAccessory.Headphones,
	},
	[AnnieAvatarPresetId.CoralDrop]: {
		kind: AssistantAvatarKind.Character,
		shape: AssistantAvatarShape.Drop,
		color: AssistantAvatarColor.Coral,
		eyes: AssistantAvatarEyes.Happy,
		accessory: AssistantCharacterAccessory.Flower,
	},
	[AnnieAvatarPresetId.LongAuburn]: human({
		skin_tone: AssistantAvatarSkinTone.Tone2,
		hair: AssistantAvatarHair.Long,
		hair_color: AssistantAvatarHairColor.Auburn,
		color: AssistantAvatarColor.Mint,
	}),
	[AnnieAvatarPresetId.ChestnutBun]: human({
		skin_tone: AssistantAvatarSkinTone.Tone4,
		hair: AssistantAvatarHair.Bun,
		hair_color: AssistantAvatarHairColor.Chestnut,
		color: AssistantAvatarColor.Gold,
		accessory: AssistantHumanAccessory.Cap,
	}),
	[AnnieAvatarPresetId.ShortBeard]: human({ color: AssistantAvatarColor.Sky, facial_hair: AssistantAvatarFacialHair.Beard }),
	[AnnieAvatarPresetId.BuzzMustache]: human({
		skin_tone: AssistantAvatarSkinTone.Tone6,
		hair: AssistantAvatarHair.Buzz,
		hair_color: AssistantAvatarHairColor.Black,
		facial_hair: AssistantAvatarFacialHair.Mustache,
		color: AssistantAvatarColor.Mint,
		accessory: AssistantHumanAccessory.HearingAid,
	}),
	[AnnieAvatarPresetId.CurlyGlasses]: human({
		skin_tone: AssistantAvatarSkinTone.Tone5,
		hair: AssistantAvatarHair.Curly,
		hair_color: AssistantAvatarHairColor.Black,
		accessory: AssistantHumanAccessory.Glasses,
	}),
	[AnnieAvatarPresetId.PinkShades]: human({
		skin_tone: AssistantAvatarSkinTone.Tone1,
		hair_color: AssistantAvatarHairColor.Pink,
		color: AssistantAvatarColor.Midnight,
		eyes: AssistantAvatarEyes.Wink,
		accessory: AssistantHumanAccessory.Shades,
	}),
	[AnnieAvatarPresetId.HalloweenWitch]: HALLOWEEN_WITCH_AVATAR,
	[AnnieAvatarPresetId.HalloweenPumpkin]: HALLOWEEN_PUMPKIN_AVATAR,
	[AnnieAvatarPresetId.HalloweenGhost]: {
		kind: AssistantAvatarKind.Character,
		shape: AssistantAvatarShape.Ghost,
		color: AssistantAvatarColor.White,
		eyes: AssistantAvatarEyes.Dots,
		accessory: AssistantCharacterAccessory.None,
	},
	[AnnieAvatarPresetId.HalloweenVampire]: {
		kind: AssistantAvatarKind.Character,
		shape: AssistantAvatarShape.Blob,
		color: AssistantAvatarColor.Midnight,
		eyes: AssistantAvatarEyes.Dots,
		accessory: AssistantCharacterAccessory.Vampire,
	},
};

// The pumpkin leads: it is Halloween week's default look.
const HALLOWEEN_PRESET_IDS: readonly AnnieAvatarPresetId[] = [
	AnnieAvatarPresetId.HalloweenPumpkin,
	AnnieAvatarPresetId.HalloweenWitch,
	AnnieAvatarPresetId.HalloweenGhost,
	AnnieAvatarPresetId.HalloweenVampire,
];

const toPreset = (id: AnnieAvatarPresetId): AnnieAvatarPreset => ({ id, config: ANNIE_AVATAR_PRESET_CONFIGS[id] });

/** The year-round "Start from" looks. */
export const ANNIE_AVATAR_PRESETS: AnnieAvatarPreset[] = Object.values(AnnieAvatarPresetId)
	.filter((id) => !HALLOWEEN_PRESET_IDS.includes(id))
	.map(toPreset);

/** The Halloween costumes, offered in their own section all year (first in Halloween week). */
export const HALLOWEEN_AVATAR_PRESETS: AnnieAvatarPreset[] = HALLOWEEN_PRESET_IDS.map(toPreset);
